import { oneOf } from "@postdaily/api/lib/one-of";
import type { Outputs } from "@postdaily/api/routers/index";
import {
  type MediaSortInput,
  mediaSortSchema,
} from "@postdaily/api/schemas/media";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@postdaily/ui/components/alert-dialog";
import { Button } from "@postdaily/ui/components/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@postdaily/ui/components/empty";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { Spinner } from "@postdaily/ui/components/spinner";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ImageIcon } from "lucide-react";
import {
  parseAsBoolean,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from "nuqs";
import {
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { useComposer } from "@/components/composer/composer-dialog-provider";
import { AddToCollectionDialog } from "@/components/media/add-to-collection-dialog";
import { AgentUploadBanner } from "@/components/media/agent-upload-banner";
import { BulkActionBar } from "@/components/media/bulk-action-bar";
import { CollectionChips } from "@/components/media/collection-chips";
import type { MediaCardActions } from "@/components/media/media-card";
import { MediaGrid } from "@/components/media/media-grid";
import { MediaHero } from "@/components/media/media-hero";
import { MediaLightbox } from "@/components/media/media-lightbox";
import {
  MEDIA_STATUS_FILTERS,
  type MediaDensity,
  type MediaLayout,
  type MediaStatusFilter,
  MediaToolbar,
} from "@/components/media/media-toolbar";
import { StorageMeter } from "@/components/media/storage-meter";
import { UploadDropzone } from "@/components/media/upload-dropzone";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { track } from "@/lib/analytics";
import type { MediaAsset } from "@/lib/media";
import { useMediaSelectionStore } from "@/stores/media-selection-store";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/media")({
  component: RouteComponent,
});

type Workspace = NonNullable<
  ReturnType<typeof useCurrentWorkspace>["currentWorkspace"]
>;
type BillingState = Outputs["billing"]["state"];

const layoutValues = ["grid", "masonry"] as const;
const densityValues = ["comfortable", "compact"] as const;

function RouteComponent() {
  const { currentWorkspace, isLoading, setCurrentWorkspaceId, workspaces } =
    useCurrentWorkspace();
  // An AI app's upload link names its workspace (`list_media` uploadUrl).
  // Switch to it only when the signed-in person belongs to it, and hold the
  // page until the switch lands so a file never goes to the wrong workspace.
  const [{ workspace: linkedWorkspaceId }] = useQueryStates({
    workspace: parseAsString,
  });
  const linkedWorkspace = linkedWorkspaceId
    ? workspaces.find((workspace) => workspace.id === linkedWorkspaceId)
    : undefined;

  useEffect(() => {
    if (linkedWorkspace && currentWorkspace?.id !== linkedWorkspace.id) {
      setCurrentWorkspaceId(linkedWorkspace.id);
    }
  }, [currentWorkspace?.id, linkedWorkspace, setCurrentWorkspaceId]);

  if (
    !currentWorkspace ||
    (linkedWorkspace && currentWorkspace.id !== linkedWorkspace.id)
  ) {
    return <Skeleton className="h-72 w-full" />;
  }

  return (
    <MediaLibrary
      linkedWorkspaceMissing={
        Boolean(linkedWorkspaceId) && !isLoading && !linkedWorkspace
      }
      workspace={currentWorkspace}
    />
  );
}

function roleCanManageMedia(role: Workspace["role"]) {
  return role === "owner" || role === "admin" || role === "editor";
}

function uploadDisabledReason({
  billingState,
  canManage,
}: {
  billingState?: BillingState;
  canManage: boolean;
}) {
  if (!canManage) {
    return "Owner, admin, or editor role required.";
  }
  if (!billingState) {
    return "Billing limits are still loading.";
  }
  const remaining =
    billingState.limits.maxMediaStorageBytes -
    billingState.usage.mediaStorageBytes;
  if (remaining <= 0) {
    return `${billingState.planName} plan media storage is full.`;
  }
  return undefined;
}

function copyLink(asset: MediaAsset) {
  if (!asset.previewUrl) {
    toast.error("No shareable link available yet.");
    return;
  }
  navigator.clipboard
    .writeText(asset.previewUrl)
    .then(() => toast.success("Link copied"))
    .catch(() => toast.error("Unable to copy link"));
}

function downloadAsset(asset: MediaAsset) {
  if (!asset.previewUrl) {
    toast.error("Download unavailable.");
    return;
  }
  const link = document.createElement("a");
  link.href = asset.previewUrl;
  link.download = asset.originalFilename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function readStoredPreference<T extends string>(
  key: string,
  fallback: T,
  allowed: readonly T[],
): T {
  if (typeof window === "undefined") {
    return fallback;
  }
  try {
    const stored = window.localStorage.getItem(key);
    if (stored && oneOf(allowed)(stored)) {
      return stored;
    }
  } catch {
    // ignore storage failures (private mode, quota)
  }
  return fallback;
}

/** localStorage-backed view preference (this route tree is ssr:false, so the
 *  lazy initializer only ever runs in the browser). */
function usePreference<T extends string>(
  key: string,
  fallback: T,
  allowed: readonly T[],
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() =>
    readStoredPreference(key, fallback, allowed),
  );

  const update = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, next);
      } catch {
        // ignore storage failures (private mode, quota)
      }
    },
    [key],
  );

  return [value, update];
}

function MediaLibrary({
  linkedWorkspaceMissing,
  workspace,
}: {
  /** The page was opened from an upload link for a workspace you can't use. */
  linkedWorkspaceMissing: boolean;
  workspace: Workspace;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { openComposer } = useComposer();
  const workspaceId = workspace.id;
  const canManage = roleCanManageMedia(workspace.role);

  const [filters, setFilters] = useQueryStates({
    collectionId: parseAsString,
    favorite: parseAsBoolean.withDefault(false),
    search: parseAsString.withDefault(""),
    sort: parseAsStringEnum<MediaSortInput>([
      ...mediaSortSchema.options,
    ]).withDefault("newest"),
    status: parseAsStringEnum<MediaStatusFilter>([
      ...MEDIA_STATUS_FILTERS,
    ]).withDefault("ready"),
    type: parseAsStringEnum(["all", "image", "video", "document"]).withDefault(
      "all",
    ),
  });
  const { collectionId, favorite, search, sort, status, type } = filters;
  const [{ upload: fromAgentLink }] = useQueryStates({
    upload: parseAsBoolean.withDefault(false),
  });
  const [uploadedCount, setUploadedCount] = useState(0);

  const [layout, setLayout] = usePreference<MediaLayout>(
    "media:layout",
    "grid",
    layoutValues,
  );
  const [density, setDensity] = usePreference<MediaDensity>(
    "media:density",
    "comfortable",
    densityValues,
  );

  // Ephemeral selection lives in a store shared by toolbar/grid/bulk bar.
  const selectMode = useMediaSelectionStore((state) => state.selectMode);
  const selectedIds = useMediaSelectionStore((state) => state.selectedIds);
  const toggleSelectMode = useMediaSelectionStore(
    (state) => state.toggleSelectMode,
  );
  const toggleSelected = useMediaSelectionStore((state) => state.toggle);
  const selectRange = useMediaSelectionStore((state) => state.selectRange);
  const clearSelection = useMediaSelectionStore((state) => state.clear);

  // Reset any stale selection from a previous visit.
  useEffect(() => {
    const { reset } = useMediaSelectionStore.getState();
    reset();
    return reset;
  }, []);

  const [lightboxAssetId, setLightboxAssetId] = useState<string | null>(null);
  const [collectionTarget, setCollectionTarget] = useState<{
    assetIds: string[];
    initialSelected: string[];
  } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<MediaAsset | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const billingQuery = useQuery(
    trpc.billing.state.queryOptions({ workspaceId }),
  );
  const statsQuery = useQuery(
    trpc.media.stats.queryOptions({
      collectionId: collectionId ?? undefined,
      favorite: favorite || undefined,
      search: search || undefined,
      status,
      workspaceId,
    }),
  );
  const collectionsQuery = useQuery(
    trpc.media.collections.list.queryOptions({ workspaceId }),
  );
  const mediaQuery = useInfiniteQuery(
    trpc.media.list.infiniteQueryOptions(
      {
        collectionId: collectionId ?? undefined,
        favorite: favorite || undefined,
        limit: 30,
        search: search || undefined,
        sort,
        status,
        type,
        workspaceId,
      },
      { getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined },
    ),
  );

  const assets = mediaQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const visibleIds = assets.map((asset) => asset.id);

  // Auto-load the next page when the sentinel nears the viewport (infinite
  // scroll); the "Load more" button below remains as a fallback.
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = mediaQuery;
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasNextPage) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: "600px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  const lightboxAsset =
    assets.find((asset) => asset.id === lightboxAssetId) ?? null;

  async function invalidateMediaData() {
    await Promise.all([
      queryClient.invalidateQueries(
        trpc.media.list.queryFilter({ workspaceId }),
      ),
      queryClient.invalidateQueries(
        trpc.media.list.infiniteQueryFilter({ workspaceId }),
      ),
      queryClient.invalidateQueries(
        trpc.media.stats.queryFilter({ workspaceId }),
      ),
      queryClient.invalidateQueries(
        trpc.media.collections.list.queryFilter({ workspaceId }),
      ),
      queryClient.invalidateQueries(
        trpc.media.usage.queryFilter({ workspaceId }),
      ),
      queryClient.invalidateQueries(
        trpc.billing.state.queryFilter({ workspaceId }),
      ),
      queryClient.invalidateQueries(
        trpc.billing.usage.queryFilter({ workspaceId }),
      ),
    ]);
  }

  const deleteMedia = useMutation(
    trpc.media.delete.mutationOptions({
      onError: (error) => toast.error(error.message),
      onSuccess: async () => {
        toast.success("Media deleted");
        setLightboxAssetId(null);
        await invalidateMediaData();
      },
    }),
  );
  const bulkDelete = useMutation(
    trpc.media.bulkDelete.mutationOptions({
      onError: (error) => toast.error(error.message),
      onSuccess: async (result) => {
        if (result.blocked.length > 0) {
          toast.warning(
            `${result.deleted.length} deleted · ${result.blocked.length} still attached to posts`,
          );
        } else {
          toast.success(`${result.deleted.length} deleted`);
        }
        clearSelection();
        await invalidateMediaData();
      },
    }),
  );
  const toggleFavorite = useMutation(
    trpc.media.toggleFavorite.mutationOptions({
      onError: (error) => toast.error(error.message),
      onSuccess: () => invalidateMediaData(),
    }),
  );
  const renameMedia = useMutation(
    trpc.media.rename.mutationOptions({
      onError: (error) => toast.error(error.message),
      onSuccess: async () => {
        toast.success("Renamed");
        await invalidateMediaData();
      },
    }),
  );
  const addToCollections = useMutation(
    trpc.media.addToCollections.mutationOptions({
      onError: (error) => toast.error(error.message),
    }),
  );
  const removeFromCollection = useMutation(
    trpc.media.removeFromCollection.mutationOptions({
      onError: (error) => toast.error(error.message),
    }),
  );
  // Tracked here rather than at the call sites, so the chips row and the
  // add-to-collection dialog are both counted.
  const createCollection = useMutation(
    trpc.media.collections.create.mutationOptions({
      onError: (error) => toast.error(error.message),
      onSuccess: () => track("media_collection_created"),
    }),
  );

  const billingState = billingQuery.data;
  const disabledReason = uploadDisabledReason({ billingState, canManage });
  const canUpload = !disabledReason;
  const remainingStorageBytes = Math.max(
    (billingState?.limits.maxMediaStorageBytes ?? 0) -
      (billingState?.usage.mediaStorageBytes ?? 0),
    0,
  );

  function handleSelect(asset: MediaAsset, event?: MouseEvent) {
    if (event?.shiftKey) {
      selectRange(asset.id, visibleIds);
    } else {
      toggleSelected(asset.id);
    }
  }

  async function applyCollectionDiff(diff: {
    add: string[];
    remove: string[];
  }) {
    if (!collectionTarget) {
      return;
    }
    try {
      if (diff.add.length > 0) {
        await addToCollections.mutateAsync({
          collectionIds: diff.add,
          mediaAssetIds: collectionTarget.assetIds,
          workspaceId,
        });
      }
      // Removals only apply to a single asset (bulk starts from an empty set).
      const [assetId] = collectionTarget.assetIds;
      if (assetId) {
        await Promise.all(
          diff.remove.map((collectionId) =>
            removeFromCollection.mutateAsync({
              collectionId,
              mediaAssetId: assetId,
              workspaceId,
            }),
          ),
        );
      }
      toast.success("Collections updated");
      setCollectionTarget(null);
      await invalidateMediaData();
    } catch {
      // errors surfaced via mutation onError
    }
  }

  const actions: MediaCardActions = {
    onAddToCollection: (asset) =>
      setCollectionTarget({
        assetIds: [asset.id],
        initialSelected: asset.collectionIds,
      }),
    onCopyLink: copyLink,
    onDelete: (asset) => setPendingDelete(asset),
    onDownload: downloadAsset,
    onOpen: (asset) => setLightboxAssetId(asset.id),
    onToggleFavorite: (asset) =>
      toggleFavorite.mutate({
        favorite: !asset.favorite,
        mediaAssetId: asset.id,
        workspaceId,
      }),
    onUseInPost: (asset) => openComposer({ mediaAssetIds: [asset.id] }),
  };

  const initialLoading = mediaQuery.isPending && assets.length === 0;
  const hasActiveFilters =
    search !== "" ||
    type !== "all" ||
    status !== "ready" ||
    favorite ||
    collectionId !== null;

  const emptyState =
    !initialLoading && assets.length === 0 ? (
      <Empty className="rounded-2xl border border-dashed">
        <EmptyHeader>
          <EmptyTitle className="font-heading text-2xl">
            {hasActiveFilters ? "No matching media" : "Nothing here yet"}
          </EmptyTitle>
          <EmptyDescription>
            {hasActiveFilters
              ? "No files match the current filters. Try clearing them."
              : "Upload images and video to reuse them in scheduled posts."}
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          {hasActiveFilters ? (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setFilters({
                  collectionId: null,
                  favorite: false,
                  search: "",
                  status: "ready",
                  type: "all",
                })
              }
            >
              Clear filters
            </Button>
          ) : (
            <span className="flex size-12 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
              <ImageIcon />
            </span>
          )}
        </EmptyContent>
      </Empty>
    ) : null;

  return (
    <div className="flex flex-col gap-6">
      <MediaHero
        totalCount={statsQuery.data?.total ?? null}
        actions={
          <StorageMeter
            isLoading={billingQuery.isPending}
            capBytes={billingState?.limits.maxMediaStorageBytes ?? 0}
            usedBytes={billingState?.usage.mediaStorageBytes ?? 0}
            planName={billingState?.planName ?? ""}
          />
        }
      />

      {!canManage ? (
        <Alert>
          <AlertTitle>Read-only media library</AlertTitle>
          <AlertDescription>
            Your role can view media metadata and previews but cannot upload or
            delete assets.
          </AlertDescription>
        </Alert>
      ) : null}

      {billingQuery.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Media limits unavailable</AlertTitle>
          <AlertDescription>{billingQuery.error.message}</AlertDescription>
        </Alert>
      ) : null}

      {linkedWorkspaceMissing ? (
        <Alert variant="destructive">
          <AlertTitle>This upload link is for another workspace</AlertTitle>
          <AlertDescription>
            You aren't a member of the workspace your AI app asked for, so
            nothing here would reach it. Check which PostDaily account you're
            signed in with, or ask the app for a new link.
          </AlertDescription>
        </Alert>
      ) : fromAgentLink ? (
        <AgentUploadBanner
          canUpload={canUpload}
          disabledReason={disabledReason}
          uploadedCount={uploadedCount}
          workspaceName={workspace.name}
        />
      ) : null}

      <div
        className={
          fromAgentLink && !linkedWorkspaceMissing
            ? "rounded-xl ring-2 ring-primary ring-offset-2 ring-offset-background"
            : undefined
        }
      >
        <UploadDropzone
          canUpload={canUpload}
          disabledReason={disabledReason}
          maxFileBytes={billingState?.limits.maxMediaFileBytes ?? 0}
          onUploaded={() => setUploadedCount((count) => count + 1)}
          remainingStorageBytes={remainingStorageBytes}
          workspaceId={workspaceId}
        />
      </div>

      <CollectionChips
        activeCollectionId={collectionId}
        canManage={canManage}
        collections={collectionsQuery.data ?? []}
        favoriteActive={favorite}
        favoritesCount={statsQuery.data?.favorites ?? 0}
        isCreating={createCollection.isPending}
        isLoading={collectionsQuery.isPending || statsQuery.isPending}
        totalCount={statsQuery.data?.total ?? 0}
        onCreateCollection={(name) =>
          createCollection.mutate(
            { name, workspaceId },
            {
              onSuccess: async () => {
                toast.success("Collection created");
                await queryClient.invalidateQueries(
                  trpc.media.collections.list.queryFilter({ workspaceId }),
                );
              },
            },
          )
        }
        onSelectAll={() => setFilters({ collectionId: null, favorite: false })}
        onSelectCollection={(id) =>
          setFilters({ collectionId: id, favorite: false })
        }
        onSelectFavorites={() =>
          setFilters({ collectionId: null, favorite: true })
        }
      />

      <MediaToolbar
        counts={
          statsQuery.data?.filtered ?? {
            all: 0,
            documents: 0,
            images: 0,
            videos: 0,
          }
        }
        density={density}
        isRefreshing={mediaQuery.isRefetching}
        layout={layout}
        selectMode={selectMode}
        search={search}
        sort={sort}
        status={status}
        type={type}
        onDensityChange={setDensity}
        onLayoutChange={setLayout}
        onRefresh={() => mediaQuery.refetch()}
        onSearchChange={(value) => setFilters({ search: value || null })}
        onSortChange={(value) => setFilters({ sort: value })}
        onStatusChange={(value) => setFilters({ status: value })}
        onToggleSelectMode={toggleSelectMode}
        onTypeChange={(value) => setFilters({ type: value })}
      />

      <MediaGrid
        actions={actions}
        assets={initialLoading ? undefined : assets}
        canManage={canManage}
        deletingAssetId={deleteMedia.variables?.mediaAssetId ?? null}
        density={density}
        emptyState={emptyState}
        error={mediaQuery.error}
        isLoading={initialLoading}
        layout={layout}
        onSelect={handleSelect}
        selectable={selectMode}
        selectedIds={selectedIds}
      />

      {mediaQuery.hasNextPage ? (
        <div ref={loadMoreRef} className="flex justify-center pb-2">
          <Button
            type="button"
            variant="outline"
            disabled={mediaQuery.isFetchingNextPage}
            onClick={() => void mediaQuery.fetchNextPage()}
          >
            {mediaQuery.isFetchingNextPage ? (
              <>
                <Spinner data-icon="inline-start" /> Loading…
              </>
            ) : (
              "Load more"
            )}
          </Button>
        </div>
      ) : null}

      <BulkActionBar
        canManage={canManage}
        isDeleting={bulkDelete.isPending}
        selectedCount={selectedIds.length}
        onAddToCollection={() =>
          setCollectionTarget({ assetIds: selectedIds, initialSelected: [] })
        }
        onAddToPost={() => {
          openComposer({ mediaAssetIds: selectedIds });
        }}
        onClear={clearSelection}
        onDelete={() => setBulkDeleteOpen(true)}
        onDownload={() => {
          const selected = new Set(selectedIds);
          for (const asset of assets) {
            if (selected.has(asset.id)) {
              downloadAsset(asset);
            }
          }
        }}
      />

      <MediaLightbox
        // Remount per asset so the rename input re-seeds from the new asset.
        key={lightboxAssetId ?? "none"}
        asset={lightboxAsset}
        canManage={canManage}
        open={lightboxAssetId !== null}
        workspaceId={workspaceId}
        onCopyLink={copyLink}
        onDelete={(asset) => setPendingDelete(asset)}
        onDownload={downloadAsset}
        onOpenChange={(open) => {
          if (!open) {
            setLightboxAssetId(null);
          }
        }}
        onOpenPost={(postId) => {
          setLightboxAssetId(null);
          openComposer({ postId });
        }}
        onRename={(filename) => {
          if (lightboxAsset) {
            renameMedia.mutate({
              filename,
              mediaAssetId: lightboxAsset.id,
              workspaceId,
            });
          }
        }}
        onUseInPost={(asset) => {
          setLightboxAssetId(null);
          openComposer({ mediaAssetIds: [asset.id] });
        }}
      />

      {collectionTarget ? (
        <AddToCollectionDialog
          assetCount={collectionTarget.assetIds.length}
          collections={collectionsQuery.data ?? []}
          initialSelected={collectionTarget.initialSelected}
          isSaving={
            addToCollections.isPending || removeFromCollection.isPending
          }
          open
          onApply={applyCollectionDiff}
          onCreateCollection={async (name) => {
            const created = await createCollection.mutateAsync({
              name,
              workspaceId,
            });
            await queryClient.invalidateQueries(
              trpc.media.collections.list.queryFilter({ workspaceId }),
            );
            return created.id;
          }}
          onOpenChange={(open) => {
            if (!open) {
              setCollectionTarget(null);
            }
          }}
        />
      ) : null}

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete media asset?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.originalFilename} will be removed from this
              workspace. Assets attached to a post can't be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (pendingDelete) {
                  deleteMedia.mutate({
                    mediaAssetId: pendingDelete.id,
                    workspaceId,
                  });
                }
                setPendingDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedIds.length} assets?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Selected assets will be removed from this workspace. Assets
              attached to a post are skipped.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                bulkDelete.mutate({ mediaAssetIds: selectedIds, workspaceId });
                setBulkDeleteOpen(false);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
